import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { JOURNEY_STAGES } from "../../demo/demoData";

const toneRing = {
  neutral: "border-white/12",
  brand: "border-primary/40",
  action: "border-signal-amber/40",
};
const toneActive = {
  neutral: "bg-white/10 border-white/30",
  brand: "bg-primary/15 border-primary/60",
  action: "bg-signal-amber/15 border-signal-amber/60",
};

function prefersReduced() {
  return typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function InteractionJourney({ variant = "full" }) {
  const [selected, setSelected] = useState(0);
  const [pulse, setPulse] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (prefersReduced()) return undefined;
    timer.current = setInterval(() => {
      setPulse((p) => (p + 1) % JOURNEY_STAGES.length);
    }, 1400);
    return () => clearInterval(timer.current);
  }, []);

  const stage = JOURNEY_STAGES[selected];

  return (
    <div className="rounded-lg border border-white/10 bg-navy-900/60 p-5 sm:p-7">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">The control &amp; evidence path</p>
        <span className="text-xs text-muted-foreground">Click any stage · illustrative</span>
      </div>

      {/* Desktop horizontal */}
      <div className="mt-5 hidden lg:flex items-stretch gap-1.5">
        {JOURNEY_STAGES.map((s, i) => (
          <React.Fragment key={s.key}>
            <button
              onClick={() => setSelected(i)}
              aria-pressed={selected === i}
              className={`flex-1 text-left rounded-xl border px-3 py-3 transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                selected === i ? toneActive[s.tone] : `${toneRing[s.tone]} bg-white/[0.02] hover:bg-white/[0.05]`
              } ${pulse === i ? "shadow-glow" : ""}`}
            >
              <span className="text-[0.78rem] font-semibold leading-tight text-foreground block">{s.label}</span>
            </button>
            {i < JOURNEY_STAGES.length - 1 && (
              <div className="flex items-center text-primary/60 shrink-0">
                <ArrowRight className={`h-4 w-4 ${pulse === i ? "text-primary animate-pulse-soft" : ""}`} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Mobile vertical */}
      <div className="mt-5 lg:hidden space-y-1.5">
        {JOURNEY_STAGES.map((s, i) => (
          <React.Fragment key={s.key}>
            <button
              onClick={() => setSelected(i)}
              aria-pressed={selected === i}
              className={`w-full text-left rounded-xl border px-4 py-3 transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                selected === i ? toneActive[s.tone] : `${toneRing[s.tone]} bg-white/[0.02]`
              }`}
            >
              <span className="text-sm font-semibold text-foreground">{s.label}</span>
            </button>
            {i < JOURNEY_STAGES.length - 1 && (
              <div className="flex justify-center text-primary/60"><ArrowDown className="h-4 w-4" /></div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Detail panel */}
      <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] p-5">
        <h4 className="text-lg font-semibold text-foreground">{stage.label}</h4>
        {stage.note && (
          <p className="mt-1 text-xs text-signal-amber">{stage.note}</p>
        )}
        <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ["What enters", stage.enters],
            ["What norse3 does", stage.does],
            ["Decision / output", stage.output],
            ["What is recorded", stage.recorded],
            ["Where humans review", stage.human],
          ].map(([k, v]) => (
            <div key={k}>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">{k}</dt>
              <dd className="mt-1 text-sm text-foreground leading-relaxed">{v}</dd>
            </div>
          ))}
        </dl>
      </div>

      {variant === "full" && (
        <p className="mt-4 text-sm text-muted-foreground">
          Bifröst connects and routes approved interactions, Aegis performs detection and policy
          evaluation, and Heimdall provides oversight and evidence. The underlying AI model or
          communication platform is <span className="text-foreground font-medium">not part of norse3</span>.
        </p>
      )}
    </div>
  );
}
